import { Controller } from "@hotwired/stimulus"

function safeParseFloat(value) {
  return parseFloat(String(value).replace(",", "."), 10)
}

// Connects to data-controller="payment-form"
export default class extends Controller {

  static targets = [
    "balance",
    "resetButton",
    "typeSelectField",
    "creditSelectWrapper",
    "creditSelectField",
    "amountField",
    "feeFields",
    "chargeFeesCheckbox",
    "lateFeesField",
    "recoveryFeeField",
    "totalPayableField",
    "creditAmount",
    "dateField",
    "submitButton",
    "overpaymentWarning",
  ]

  static values = { creditId: Number, chequeId: Number, balance: Number}

  get creditTypeSelected() {
    return this.typeSelectFieldTarget.value == this.creditIdValue
  }

  get chequeTypeSelected() {
    return this.typeSelectFieldTarget.value == this.chequeIdValue
  }

  get balanceValue() {
    return safeParseFloat(this.balanceTarget.value)
  }

  get selectedCreditAmount() {
    if (!this.hasCreditSelectFieldTarget || !this.creditSelectFieldTarget.value) return null

    const selectedOption = this.creditSelectFieldTarget.options[this.creditSelectFieldTarget.selectedIndex]
    const amount = safeParseFloat(selectedOption.dataset.amount)

    return isNaN(amount) ? null : amount
  }

  get creditPaymentMax() {
    const creditAmount = this.selectedCreditAmount

    if (creditAmount === null) return this.balanceValue

    return Math.min(creditAmount, this.balanceValue)
  }

  selectOnlyCredit() {
    if (!this.hasCreditSelectFieldTarget) return
    if (this.creditSelectFieldTarget.value) return

    const creditOptions = Array.from(this.creditSelectFieldTarget.options).filter((option) => option.value)

    if (creditOptions.length === 1) {
      this.creditSelectFieldTarget.value = creditOptions[0].value
    }
  }

  connect() {
    this.selectOnlyCredit()

    if (this.creditTypeSelected && this.selectedCreditAmount !== null) {
      this.amountFieldTarget.value = this.creditPaymentMax
    }

    this.enforceCreditMax()
    this.updateOverpaymentWarning()
  }

  onTypeChange(e) {

    // Only proceed with credit-related operations if the targets exist
    if (this.hasCreditSelectWrapperTarget && this.hasCreditSelectFieldTarget) {
      if (this.creditTypeSelected) {
        this.creditSelectWrapperTarget.classList.remove('hidden')
        this.creditSelectFieldTarget.required = true
        this.selectOnlyCredit()
        if (this.creditSelectFieldTarget.value) {
          this.amountFieldTarget.value = this.creditPaymentMax
        }
      } else {
        this.creditSelectWrapperTarget.classList.add('hidden')
        this.creditSelectFieldTarget.value = ''
        this.creditSelectFieldTarget.required = false
        this.amountFieldTarget.value = this.balanceValue
      }
    }

    this.enforceCreditMax()
    this.updateOverpaymentWarning()
  }

  onCreditChange(e) {
    if (e.target.value) {
      this.amountFieldTarget.value = this.creditPaymentMax
      this.amountFieldTarget.max = this.creditPaymentMax
    } else {
      this.amountFieldTarget.value = this.balanceValue
    }
    this.enforceCreditMax()
  }

  setFeeAmounts() {
    const paymentAmount = safeParseFloat(this.amountFieldTarget.value)
    let payableSafeAmount

    if (!this.chargeFeesCheckboxTarget.checked) {
      payableSafeAmount = paymentAmount.toFixed(2)
    } else {
      const lateFees = safeParseFloat(this.lateFeesFieldTarget.value)
      const recoveryFee = safeParseFloat(this.recoveryFeeFieldTarget.value)
      const totalPayable = lateFees + recoveryFee + paymentAmount
      payableSafeAmount = isNaN(totalPayable) ? 0 : totalPayable.toFixed(2)
    }

    // this.totalPayableFieldTarget.value = payableSafeAmount
    this.totalPayableFieldTarget.textContent = `${payableSafeAmount} €`
    this.submitButtonTarget.value = `Encaisser ${payableSafeAmount} €`
  }

  onAmountChange() {
    this.updateOverpaymentWarning()
  }

  updateOverpaymentWarning() {
    if (!this.hasOverpaymentWarningTarget) return

    const amount = safeParseFloat(this.amountFieldTarget.value)
    const balance = safeParseFloat(this.balanceTarget.value)
    const isOverpaying = !isNaN(amount) && !isNaN(balance) && amount > balance
    const isCreditType = this.creditTypeSelected

    this.overpaymentWarningTarget.classList.toggle('hidden', !isOverpaying || isCreditType)
  }

  // For credit-type payments, enforce max on the amount field; remove it for other types
  enforceCreditMax() {
    if (this.creditTypeSelected) {
      this.amountFieldTarget.max = this.creditPaymentMax
    } else {
      this.amountFieldTarget.removeAttribute('max')
    }
  }

  onDateChange(e) {
  }

  resetAmount() {
    this.amountFieldTarget.value = this.balanceTarget.value
    this.setFeeAmounts()
  }

  toggleEnforceFees(e) {
    const enforceFees = e.target.checked
    this.feeFieldsTarget.classList.toggle('hidden', !enforceFees)
    this.setFeeAmounts()
    this.updateConfirmationMessage()
  }

  updateConfirmationMessage() {
    const amount = this.totalPayableFieldTarget.textContent
    const enforceFees = this.chargeFeesCheckboxTarget.checked
    this.submitButtonTarget.setAttribute('data-turbo-confirm', enforceFees ? `Êtes-vous sûr de vouloir encaisser ce paiement AVEC les frais de retard ?\nMontant à encaisser : ${amount}` : `Êtes-vous sûr de vouloir encaisser ce paiement SANS les frais de retard ?\nMontant à encaisser : ${amount}`)
  }

  onLateFeesChange() {
    this.setFeeAmounts()
    this.updateConfirmationMessage()
  }
}
